import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { pdf } from '@react-pdf/renderer';
import { sectionTabsCSS } from './SectionTabs.css';
import BuroCreditoPDF from './BuroCreditoPDF';
import { url } from '../../../../env';

const getCreditrustStatus = (persona) => {
    const registered = persona?.creditrust_registered;
    const hasSyntage = persona?.syntage_status;
    const hasBuro = persona?.buro_status;

    if (registered && hasSyntage && hasBuro) {
        return { label: 'Listo', severity: 'success' };
    }
    if (registered) {
        return { label: 'Parcial', severity: 'warning' };
    }
    return { label: 'Error', severity: 'danger' };
};

const formatSectionLabel = (section) => {
    if (section === 'REFERENCES') return 'Referencias';
    const lower = section.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
};

// `docs_sections` (respuesta de BuroController::getUsersReport, alimentada
// por DocumentCompletenessService::evaluate()) trae los campos con la key de
// columna real de UserFile (p. ej. "edos_cta"), no con el nombre de
// categoría en mayúsculas que ya usan `categoriaSeleccionada` / el objeto
// `categories` de ContenidoPrincipal (viene de /pdfs/list, alimentado por su
// propio $categoryMapping en PdfDownloadController). Este mapa traduce de
// uno a otro para no romper el click-through a los documentos: es el mismo
// diccionario que ya usa BuroController::getUsersReport para construir el
// `sections` legado (fieldToCategory), así que un mismo documento cae en el
// mismo folder de siempre.
const FIELD_KEY_TO_LEGACY_CATEGORY = {
    edos_cta: 'EDOS CUENTA',
    edos_cta_aval: 'EDO CUENTA AVAL',
    compraante_fiscal: 'COMPROBANTE FISCAL',
    comprobante_operativo: 'COMPROBANTE OPERATIVO',
    comprobante_aval: 'COMPROBANTE AVAL',
    references: 'REFERENCIAS',
    estados_financieros: 'ESTADOS FINANCIEROS',
    acta_constitutiva: 'ACTA CONSTI',
    poderes: 'PODERES',
    registro_publico: 'REGISTRO PUBLICO',
    identifications: 'IDENTIFICACIONES',
};

// Construye los items del menú "Documentos" a partir de `docs_sections`
// (el cálculo correcto, con mínimos reales). Uno por campo, no por sección,
// porque es a nivel de campo donde vive el conteo ("4 de 6").
const documentosCatsDesdeDocsSections = (docsSections) => {
    const items = [];
    (docsSections || []).forEach((section) => {
        (section.fields || []).forEach((field) => {
            const legacyKey = FIELD_KEY_TO_LEGACY_CATEGORY[field.key];
            if (!legacyKey) return; // sin mapeo conocido a la vista de archivos: se omite, no se rompe nada
            items.push({
                key: legacyKey,
                label: `${field.label} (${field.count} de ${field.min})`,
                state: field.state,
            });
        });
    });
    return items;
};

export default function SectionTabs({ categoriaSeleccionada, onCategoriaChange, persona, dataBuro, toast }) {
    const isPF = persona?.persona_subtype === 'PF';
    const creditrustStatus = getCreditrustStatus(persona);

    // `docs_sections` es el cálculo correcto (mínimos reales por subtipo,
    // ver DocumentCompletenessService). Si no viniera en la respuesta, se cae
    // a `persona.sections` (el catálogo viejo, sin mínimos) sin romper nada.
    const hasDocsSections = Array.isArray(persona?.docs_sections) && persona.docs_sections.length > 0;

    const documentosCats = hasDocsSections
        ? documentosCatsDesdeDocsSections(persona.docs_sections)
        : (persona?.sections || []).map((section) => ({
            key: section === 'REFERENCES' ? 'REFERENCIAS' : section,
            label: formatSectionLabel(section),
        }));

    const isDocumentosCurrent = documentosCats.some(
        (d) => d.key === categoriaSeleccionada
    );

    const tabs = [
        { key: 'COTIZACION', label: 'Cotización', icon: 'pi pi-file-edit' },
        { key: 'SYNTAGE', label: 'Syntage', icon: 'pi pi-database' },
        ...(!isPF
            ? [{
                key: 'CREDITRUST',
                label: 'Creditrust',
                icon: 'pi pi-shield',
                status: creditrustStatus,
            }]
            : []),
        { key: 'BURO DE CREDITO', label: 'Buró de crédito', icon: 'pi pi-check-circle' },
        { key: 'DOCUMENTOS', label: 'Documentos', icon: 'pi pi-folder', expander: true },
    ];

    const [menuOpen, setMenuOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
    const docsBtnRef = useRef(null);
    const menuRef = useRef(null);
    const [downloadingBuro, setDownloadingBuro] = useState(false);

    const hasBuroData = (d) => Boolean(d && d.cards && d.tipo_persona);

    // El render del PDF es CPU puro y síncrono. Se manda a un worker para no
    // congelar la pestaña; si el worker no está disponible (navegador viejo o
    // fallo al bundlear) se cae al hilo principal como respaldo.
    const PDF_TIMEOUT_MS = 180000;

    const renderBuroPdfInWorker = (payload) =>
        new Promise((resolve, reject) => {
            let worker;
            try {
                worker = new Worker(new URL('./buroPdf.worker.js', import.meta.url));
            } catch (e) {
                reject(Object.assign(new Error('worker-no-disponible'), { canFallback: true }));
                return;
            }
            let settled = false;
            const done = (fn, arg) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                worker.terminate();
                fn(arg);
            };
            const timer = setTimeout(
                () => done(reject, new Error('timeout')),
                PDF_TIMEOUT_MS
            );
            worker.onmessage = (e) => {
                if (e.data?.ok) done(resolve, e.data.blob);
                else done(reject, new Error(e.data?.error || 'render-fallido'));
            };
            worker.onerror = (e) =>
                done(reject, Object.assign(new Error(e.message || 'worker-error'), { canFallback: true }));
            worker.postMessage(payload);
        });

    const handleDescargarBuro = async () => {
        if (!persona?.id || downloadingBuro) return;
        setDownloadingBuro(true);
        try {
            let data = hasBuroData(dataBuro) ? dataBuro : null;

            if (!data) {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${url}/getReportBuro/${persona.id}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });
                data = response.data;
            }

            if (!hasBuroData(data)) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Sin datos de Buró',
                    detail: 'Este usuario aún no tiene un reporte de Buró de Crédito guardado.',
                    life: 4000,
                });
                return;
            }

            toast?.current?.show({
                severity: 'info',
                summary: 'Generando PDF',
                detail: 'El reporte de Buró puede tardar unos segundos.',
                life: 4000,
            });

            const payload = { data, persona, date: Date.now() };
            let blob;
            try {
                blob = await renderBuroPdfInWorker(payload);
            } catch (workerError) {
                if (!workerError?.canFallback) throw workerError;
                console.warn('Worker de PDF no disponible, generando en el hilo principal:', workerError);
                blob = await pdf(
                    <BuroCreditoPDF data={data} persona={persona} date={new Date()} />
                ).toBlob();
            }

            const cliente = persona?.name_syntage || persona?.name || 'Cliente';
            const fileName = `Buro de credito - ${cliente} (Gestion ${persona.id}).pdf`;

            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch (error) {
            console.error('Error al generar PDF de Buró de Crédito:', error);
            toast?.current?.show({
                severity: 'error',
                summary: 'Error',
                detail:
                    error?.message === 'timeout'
                        ? 'El reporte tardó demasiado en generarse. Vuelve a intentarlo.'
                        : 'No se pudo generar el PDF del Buró de Crédito.',
                life: 5000,
            });
        } finally {
            setDownloadingBuro(false);
        }
    };

    const openMenu = () => {
        const rect = docsBtnRef.current?.getBoundingClientRect();
        if (!rect) return;
        setMenuPos({ top: rect.bottom + 6, left: rect.left });
        setMenuOpen(true);
    };

    const closeMenu = () => setMenuOpen(false);

    useEffect(() => {
        if (!menuOpen) return undefined;
        const onClickAway = (e) => {
            if (
                !menuRef.current?.contains(e.target) &&
                !docsBtnRef.current?.contains(e.target)
            ) {
                closeMenu();
            }
        };
        const onKey = (e) => {
            if (e.key === 'Escape') closeMenu();
        };
        const onScrollOrResize = () => closeMenu();
        document.addEventListener('mousedown', onClickAway);
        window.addEventListener('keydown', onKey);
        window.addEventListener('resize', onScrollOrResize);
        window.addEventListener('scroll', onScrollOrResize, true);
        return () => {
            document.removeEventListener('mousedown', onClickAway);
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('resize', onScrollOrResize);
            window.removeEventListener('scroll', onScrollOrResize, true);
        };
    }, [menuOpen]);

    const handleTabClick = (tab) => {
        if (tab.key === 'DOCUMENTOS') {
            if (documentosCats.length === 0) return;
            if (menuOpen) closeMenu();
            else openMenu();
            return;
        }
        onCategoriaChange(tab.key);
    };

    const handleSubSelect = (sub) => {
        onCategoriaChange(sub.key);
        closeMenu();
    };

    return (
        <div css={sectionTabsCSS()}>
            <div
                role="tablist"
                aria-label="Secciones de la gestión"
                className="st-tablist"
            >
                {tabs.map((tab) => {
                    const isDocs = tab.key === 'DOCUMENTOS';
                    const isCurrent = isDocs
                        ? isDocumentosCurrent
                        : categoriaSeleccionada === tab.key;
                    const className = [
                        'st-tab',
                        isCurrent && 'is-current',
                        isDocs && 'st-tab--has-menu',
                    ]
                        .filter(Boolean)
                        .join(' ');
                    return (
                        <button
                            key={tab.key}
                            ref={isDocs ? docsBtnRef : undefined}
                            type="button"
                            role={isDocs ? 'button' : 'tab'}
                            aria-selected={!isDocs ? isCurrent : undefined}
                            aria-current={isCurrent ? 'step' : undefined}
                            aria-haspopup={isDocs ? 'menu' : undefined}
                            aria-expanded={isDocs ? menuOpen : undefined}
                            className={className}
                            onClick={() => handleTabClick(tab)}
                        >
                            <i className={tab.icon} aria-hidden="true" />
                            <span>{tab.label}</span>
                            {tab.status && (
                                <span
                                    className={`st-tab__status st-tab__status--${tab.status.severity}`}
                                >
                                    {tab.status.label}
                                </span>
                            )}
                            {isDocs && (
                                <i
                                    className={`pi pi-chevron-${menuOpen ? 'up' : 'down'} st-tab__chevron`}
                                    aria-hidden="true"
                                />
                            )}
                        </button>
                    );
                })}

                <button
                    type="button"
                    className="st-tab st-tab--download"
                    onClick={handleDescargarBuro}
                    disabled={downloadingBuro}
                    title="Descargar el reporte completo de Buró de Crédito en PDF"
                >
                    <i
                        className={
                            downloadingBuro
                                ? 'pi pi-spin pi-spinner'
                                : 'pi pi-download'
                        }
                        aria-hidden="true"
                    />
                    <span>
                        {downloadingBuro ? 'Generando…' : 'Descargar buró de crédito'}
                    </span>
                </button>
            </div>

            {menuOpen && documentosCats.length > 0 && (
                <div
                    ref={menuRef}
                    role="menu"
                    aria-label="Secciones de documentos"
                    className="st-menu"
                    style={{ top: menuPos.top, left: menuPos.left }}
                >
                    {documentosCats.map((sub) => {
                        const isCurrent = categoriaSeleccionada === sub.key;
                        return (
                            <button
                                key={sub.key}
                                type="button"
                                role="menuitem"
                                aria-current={isCurrent ? 'true' : undefined}
                                className={`st-menu__item${isCurrent ? ' is-current' : ''}`}
                                onClick={() => handleSubSelect(sub)}
                            >
                                <i className="pi pi-file" aria-hidden="true" />
                                <span>{sub.label}</span>
                                {isCurrent && (
                                    <i
                                        className="pi pi-check st-menu__check"
                                        aria-hidden="true"
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
